import type { DescriptionLang } from "@hisense/shared";
import type { QuotationLineDraft } from "./QuotationLinesEditor";

/*
 * Bố cục trang Sửa báo giá (19/09/2026, cùng chuẩn PI và đơn hàng mẫu).
 *
 * Số liệu production 19/09/2026 — 229 báo giá: người lập 100%; ngày in 99,6%; khách hàng 96%; người nhận 95%; ghi chú 12%;
 * điều kiện hiệu lực 3,5% (báo giá lập ở hệ mới đều có — câu mặc định); hiệu lực đến 1,7%; người liên hệ chọn từ danh bạ 2%.
 * 521 dòng: mã, mô tả, L/T mẫu, L/T sản xuất 100%; màu, MOQ, ảnh, đơn giá 99%; cỡ 98%; đơn vị 95%; giá EXW 84%; giá dưới MOQ
 * 74%; mã phụ 66%; ghi chú dòng 10%; MCQ 5%; giá DTD 1,9%; MSQ 0%. Bốn cột cuối là CỘT PHỤ: ẩn khi cả báo giá không dòng nào
 * dùng, tự hiện và không ẩn được khi có dòng dùng (không bao giờ giấu dữ liệu), bấm "Hiện cột phụ" để nhập.
 *
 * Bảng này là nguồn của bố cục; test bắt buộc mọi trường phần đầu và mọi trường của dòng có đúng một chỗ.
 */

/** Phần đầu báo giá đang sửa. */
export interface QuotationHeaderDraft {
  customerId: string;
  contactId: string;
  /** Bảng giá chung: không in thông tin khách hàng. */
  omitCustomer: boolean;
  attn: string;
  preparedBy: string;
  currency: string;
  remarks: string;
  issueDate: string;
  printingDate: string;
  validUntil: string;
  validityTerms: string;
  printLanguage: DescriptionLang;
}

export type QuotationGroupId = "options" | "customer" | "terms" | "notes";

/** Trường phần đầu theo nhóm. Ngôn ngữ bản in là tuỳ chọn cả chứng từ, nằm ở đầu trang cạnh tiêu đề. */
export const QUOTATION_HEADER_GROUPS: readonly { id: QuotationGroupId; fields: readonly (keyof QuotationHeaderDraft)[] }[] = [
  { id: "options", fields: ["printLanguage"] },
  { id: "customer", fields: ["customerId", "omitCustomer", "contactId", "attn"] },
  { id: "terms", fields: ["issueDate", "printingDate", "validUntil", "currency", "preparedBy"] },
  { id: "notes", fields: ["remarks", "validityTerms"] },
];

/**
 * Cột luôn hiện, theo thứ tự bản in: nhận diện → giá chào → MOQ và thời gian. Giá đứng ngay sau đơn vị: gõ giá là việc chính
 * của trang này, bảng hẹp hơn khung thì giá vẫn nằm gần cột mã đang dính bên trái.
 */
export const QUOTATION_MAIN_COLUMNS = [
  "image", "productCode", "subRef", "description", "color", "size", "unit",
  "unitPrice", "exwPrice", "underMoqPrice", "moq", "sampleLeadTime", "bulkLeadTime",
] as const;
/** Cột phụ — ít dùng (xem số liệu đầu tệp). */
export const QUOTATION_SIDE_COLUMNS = ["mcq", "msq", "dtdPrice", "remark"] as const;
export type QuotationColumn = (typeof QUOTATION_MAIN_COLUMNS)[number] | (typeof QUOTATION_SIDE_COLUMNS)[number];

/** Trường dữ liệu của dòng mà mỗi cột mang. Cột mô tả sửa bản của ngôn ngữ in (tiếng Anh hoặc tiếng Hoa). */
export const QUOTATION_COLUMN_FIELDS: Record<QuotationColumn, readonly (keyof QuotationLineDraft)[]> = {
  image: ["imageUrl"], productCode: ["productCode"], subRef: ["subRef"], description: ["description", "descriptionZh"],
  color: ["color"], size: ["size"], unit: ["unit"], unitPrice: ["unitPrice"], exwPrice: ["exwPrice"], underMoqPrice: ["underMoqPrice"],
  moq: ["moq"], sampleLeadTime: ["sampleLeadTime"], bulkLeadTime: ["bulkLeadTime"],
  mcq: ["mcq"], msq: ["msq"], dtdPrice: ["dtdPrice"], remark: ["remark"],
};
/**
 * Trường của dòng KHÔNG có ô: máy giữ và gửi ngược nguyên vẹn khi lưu — id dòng, sản phẩm danh mục, lựa chọn đơn vị & giá, và
 * số lượng (báo giá chào giá MỘT đơn vị, luôn là 1).
 */
export const QUOTATION_LINE_AUTOMATIC = ["id", "productId", "unitSelection", "quantity"] as const;

/**
 * Độ rộng cố định từng cột (rem); `null` là cột mô tả, lấy phần còn lại (tối thiểu 14rem). Số liệu thật: L/T dài tối đa 5 ký tự,
 * giá 4 số lẻ ("0.0450"), MOQ ≤ 5 chữ số — nên các cột số hẹp, tiêu đề được xuống dòng. Cột chính cộng 61,75rem: khổ 1600 px
 * còn khoảng 17rem cho mô tả, không phải cuộn ngang.
 */
export const QUOTATION_COLUMN_WIDTH: Record<QuotationColumn, number | null> = {
  image: 3.25, productCode: 7, subRef: 5.5, description: null, color: 6.5, size: 5, unit: 5,
  unitPrice: 4.75, exwPrice: 4.75, underMoqPrice: 4.75, moq: 4.25, sampleLeadTime: 3.5, bulkLeadTime: 3.5,
  mcq: 4.25, msq: 4.25, dtdPrice: 4.75, remark: 12,
};
/** Cột số thứ tự và cột nút xoá dòng. */
export const QUOTATION_EDGE_COLUMNS_REM = 2 + 2;
export const QUOTATION_DESCRIPTION_MIN_REM = 14;

/** Có dòng nào dùng cột phụ không — có thì cột phụ hiện và không ẩn được. */
export const quotationSideColumnsUsed = (lines: readonly QuotationLineDraft[]) =>
  lines.some((l) => QUOTATION_SIDE_COLUMNS.some((k) => String(l[k] ?? "").trim() !== ""));
